import * as React from 'react';
import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { Switch } from '../../components/switch/Switch.tsx';
import { attractionsService, type AttractionRules } from '../../services/attractions/attractionsService.ts';
import { AttractionsFrame, COLORS, Panel, errorText, fmtDateTime } from './attractionsShared.tsx';

type Draft = {
    minReviews: string;
    minRating: string;
    hideNightlife: boolean;
    hideSaturdayOnly: boolean;
    hideMultiDay: boolean;
    multiDayHours: string;
    groupDuplicates: boolean;
    hideTransfers: boolean;
};

const draftOf = (r: AttractionRules): Draft => ({
    minReviews: String(r.minReviews ?? 0),
    minRating: String(r.minRating ?? 0),
    hideNightlife: !!r.hideNightlife,
    hideSaturdayOnly: !!r.hideSaturdayOnly,
    hideMultiDay: !!r.hideMultiDay,
    multiDayHours: String(r.multiDayHours ?? 12),
    groupDuplicates: !!r.groupDuplicates,
    hideTransfers: !!r.hideTransfers,
});

const Row: React.FC<{ title: string; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.75, borderBottom: `1px solid ${COLORS.rowBorder}`, '&:last-of-type': { borderBottom: 0 }, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{title}</Typography>
            <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: '2px' }}>{sub}</Typography>
        </Box>
        {children}
    </Box>
);

const Toggle: React.FC<{ checked: boolean; label: string; onChange: (v: boolean) => void }> = ({ checked, label, onChange }) => (
    <Switch size="medium" checked={checked} inputProps={{ 'aria-label': label }} onChange={(_: any, v: boolean) => onChange(v)} />
);

/**
 * Attractions › Rules: what every sync hides before a product reaches the
 * review queue. Saving re-applies the rules to the stored products at once.
 */
const AttractionsRulesPage = () => {
    const notify = useNotify();
    const [saved, setSaved] = useState<AttractionRules | null>(null);
    const [d, setD] = useState<Draft | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        attractionsService.rules()
            .then(r => { setSaved(r); setD(draftOf(r)); })
            .catch(e => setFailed(errorText(e, 'Could not load the rules. Reload the page to try again.')));
    }, []);

    const minReviews = Number(d?.minReviews);
    const minRating = Number(d?.minRating);
    const multiDayHours = Number(d?.multiDayHours);
    const reviewsBad = !!d && (d.minReviews.trim() === '' || !Number.isInteger(minReviews) || minReviews < 0);
    const ratingBad = !!d && (d.minRating.trim() === '' || !Number.isFinite(minRating) || minRating < 0 || minRating > 5);
    const hoursBad = !!d && (d.multiDayHours.trim() === '' || !Number.isFinite(multiDayHours) || multiDayHours <= 0);
    const valid = !!d && !reviewsBad && !ratingBad && !hoursBad;
    const dirty = !!d && !!saved && JSON.stringify(d) !== JSON.stringify(draftOf(saved));

    const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD(prev => (prev ? { ...prev, [k]: v } : prev));

    const save = async () => {
        if (!d || !valid) return;
        setSaving(true);
        try {
            const stored = await attractionsService.saveRules({
                minReviews, minRating, multiDayHours,
                hideNightlife: d.hideNightlife,
                hideSaturdayOnly: d.hideSaturdayOnly,
                hideMultiDay: d.hideMultiDay,
                groupDuplicates: d.groupDuplicates,
                hideTransfers: d.hideTransfers,
            });
            setSaved(stored);
            setD(draftOf(stored));
            notify('Rules saved and re-applied to the catalogue', { type: 'success' });
        } catch (e) {
            notify(errorText(e, 'Could not save the rules'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const numberSx = { width: 140, bgcolor: '#fff' };

    return (
        <AttractionsFrame
            title="Automatic rules"
            subtitle="Applied on every sync, before anything reaches the review queue"
            actions={d && (
                <>
                    <Button disabled={!dirty || saving} onClick={() => saved && setD(draftOf(saved))} sx={{ color: COLORS.text, height: 40 }}>Undo changes</Button>
                    <Button variant="contained" color="secondary" disabled={!dirty || !valid || saving} onClick={save}
                        sx={{ borderRadius: '20px', color: '#fff', height: 40, px: 2.5 }}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </>
            )}
        >
            {failed && <Alert severity="error" sx={{ maxWidth: 760 }}>{failed}</Alert>}
            {!failed && !d && <CircularProgress size={24} />}
            {d && (
                <Box sx={{ maxWidth: 820 }}>
                    <Panel sx={{ px: 2.5, py: 0.5 }}>
                        <Row title="Minimum reviews" sub="Hide products with fewer reviews (Viator + Tripadvisor). 0 turns the rule off.">
                            <TextField size="small" label="Reviews" value={d.minReviews} onChange={e => set('minReviews', e.target.value)} error={reviewsBad}
                                helperText={reviewsBad ? 'A whole number, 0 or more' : ' '} inputProps={{ inputMode: 'numeric' }} sx={numberSx} />
                        </Row>
                        <Row title="Minimum rating" sub="Hide products rated lower (0–5). The Viator average for Cyprus is 4.6. 0 turns the rule off.">
                            <TextField size="small" label="Stars" value={d.minRating} onChange={e => set('minRating', e.target.value)} error={ratingBad}
                                helperText={ratingBad ? 'Between 0 and 5' : ' '} inputProps={{ inputMode: 'decimal' }} sx={numberSx} />
                        </Row>
                        <Row title="Hide nightlife, bars, casinos" sub="Off-brand for Vello">
                            <Toggle checked={d.hideNightlife} label="Hide nightlife" onChange={v => set('hideNightlife', v)} />
                        </Row>
                        <Row title="Hide Saturday-only products" sub="Shabbat: products that only run on Saturdays">
                            <Toggle checked={d.hideSaturdayOnly} label="Hide Saturday-only products" onChange={v => set('hideSaturdayOnly', v)} />
                        </Row>
                        <Row title="Hide multi-day tours" sub={`Products longer than ${Number.isFinite(multiDayHours) && multiDayHours > 0 ? multiDayHours : '…'} hours`}>
                            <TextField size="small" label="Over (hours)" value={d.multiDayHours} onChange={e => set('multiDayHours', e.target.value)} error={hoursBad}
                                helperText={hoursBad ? 'More than 0' : ' '} inputProps={{ inputMode: 'numeric' }} sx={numberSx} disabled={!d.hideMultiDay} />
                            <Toggle checked={d.hideMultiDay} label="Hide multi-day tours" onChange={v => set('hideMultiDay', v)} />
                        </Row>
                        <Row title="Hide transfers" sub="Airport and hotel transfers are not attractions">
                            <Toggle checked={d.hideTransfers} label="Hide transfers" onChange={v => set('hideTransfers', v)} />
                        </Row>
                        <Row title="Group near-duplicates" sub="Same title words, supplier or meeting point → one group, review once">
                            <Toggle checked={d.groupDuplicates} label="Group near-duplicates" onChange={v => set('groupDuplicates', v)} />
                        </Row>
                    </Panel>
                    <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: 1.5, lineHeight: 1.5 }}>
                        Saving re-applies the rules to every stored product right away, without calling Viator. A product a rule removes is switched
                        off and cannot be switched on in the catalogue while the rule holds.
                        {saved?.updatedAt ? ` Last changed ${fmtDateTime(saved.updatedAt)}.` : ''}
                    </Typography>
                </Box>
            )}
        </AttractionsFrame>
    );
};

export default AttractionsRulesPage;
